import React from 'react';
import { Link } from 'react-router-dom';
import './RequestAccountDelete.css';

const RequestAccountDelete = () => {
  return (
    <div className="request-delete">
      <header className="header">
        <div className="container">
          <div className="header-left">
            <Link to="/" className="logo">
              <img src="/assets/ic_kidstrail.png" alt="KidsTrail" className="logo-icon" />
              KidsTrail
            </Link>
          </div>
          <div className="header-center">
            <div className="header-download-buttons">
              <a
                href="https://apps.apple.com/app/id6749541872"
                className="header-download-btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src="/assets/ic_applestore.png" alt="Download on App Store" className="header-store-icon" />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.kidstrail.app"
                className="header-download-btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src="/assets/ic_playstore.png" alt="Get it on Google Play" className="header-store-icon" />
              </a>
            </div>
            <div className="header-social-buttons">
              <a
                href="https://www.youtube.com/@KidsTrailCare"
                className="header-social-btn"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
              >
                <img src="/assets/ic_youtube.png" alt="YouTube" className="header-social-icon" />
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61585649347570"
                className="header-social-btn"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <img src="/assets/ic_facebook.png" alt="Facebook" className="header-social-icon" />
              </a>
              <a
                href="https://www.instagram.com/kidstrail.care/"
                className="header-social-btn"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <img src="/assets/ic_instagram.png" alt="Instagram" className="header-social-icon" />
              </a>
            </div>
          </div>
        </div>
      </header>

      <main className="main">
        <div className="container">
          <div className="delete-content">
            <h1>Account Deletion Request</h1>
            <p className="delete-intro">
              We understand that you may want to delete your KidsTrail account and associated data. 
              We provide two options for account deletion to meet your needs.
            </p>

            <section className="delete-section">
              <h2>🚀 Fast Account Deletion (Recommended)</h2>
              <div className="fast-delete">
                <p>
                  <strong>For immediate account deletion:</strong>
                </p>
                <ol>
                  <li>Open the KidsTrail app on your device</li>
                  <li>Go to Settings or Account section</li>
                  <li>Select "Delete Account"</li>
                  <li>Confirm your deletion request</li>
                </ol>
                <div className="highlight-box">
                  <p>
                    <strong>✅ Immediate Action:</strong> Your account and data will be immediately 
                    archived and made inaccessible. All data will be permanently deleted within 24-48 hours.
                  </p>
                </div>
              </div>
            </section>

            <section className="delete-section">
              <h2>📧 Email Request for Account Deletion</h2>
              <div className="email-delete">
                <p>
                  If you cannot access the app or prefer to request deletion via email, 
                  please follow these instructions:
                </p>
                
                <div className="email-instructions">
                  <h3>📨 How to Send Your Request:</h3>
                  <div className="instruction-item">
                    <p><strong>Email Address:</strong></p>
                    <p><a href="mailto:care@kidstrail.net?subject=Account Deletion Request" className="email-link">care@kidstrail.net</a></p>
                  </div>
                  
                  <div className="instruction-item">
                    <p><strong>Subject Line:</strong></p>
                    <p className="subject-line">Account Deletion Request</p>
                  </div>
                  
                  <div className="instruction-item">
                    <p><strong>Required Information in Email:</strong></p>
                    <ul>
                      <li>Your account email address</li>
                      <li>Confirmation that you want to delete your account</li>
                      <li>Reason for deletion (optional)</li>
                    </ul>
                  </div>
                </div>

                <div className="timeline-info">
                  <h3>⏰ Deletion Timeline:</h3>
                  <ul>
                    <li><strong>Email Processing:</strong> We will confirm receipt of your request within 1-7 days</li>
                    <li><strong>Data Deletion:</strong> Your account and all associated data will be permanently deleted within 30 days</li>
                    <li><strong>Confirmation:</strong> We will send you a confirmation email once deletion is complete</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="delete-section">
              <h2>📋 What Gets Deleted</h2>
              <div className="deletion-info">
                <p>When you delete your account, the following data will be permanently removed:</p>
                <ul>
                  <li>Your account profile and login information</li>
                  <li>All user-created content (custom books, drawings)</li>
                  <li>Premium subscription data (if applicable)</li>
                  <li>App usage analytics associated with your account</li>
                  <li>Any uploaded content from premium subscriptions</li>
                  <li>Communication history with our support team</li>
                </ul>
                <div className="warning-box">
                  <p>
                    <strong>⚠️ Important:</strong> Account deletion is permanent and cannot be undone. 
                    Please make sure you have backed up any content you wish to keep before proceeding.
                  </p>
                </div>
              </div>
            </section>

            <section className="delete-section">
              <h2>🔒 Privacy & Security</h2>
              <div className="privacy-info">
                <p>
                  Your privacy is important to us. During the deletion process:
                </p>
                <ul>
                  <li>Your data is immediately made inaccessible</li>
                  <li>All data is securely deleted from our servers</li>
                  <li>No personal information is retained after deletion</li>
                  <li>Deletion confirmation is provided once complete</li>
                </ul>
              </div>
            </section>

            <section className="delete-section">
              <h2>❓ Need Help?</h2>
              <div className="help-info">
                <p>
                  If you have questions about account deletion or need assistance, please contact us:
                </p>
                <div className="contact-info">
                  <p>Email: <a href="mailto:care@kidstrail.net" className="email-link">care@kidstrail.net</a></p>
                  <p>Subject: Account Deletion Help</p>
                </div>
                <p>
                  You can also visit our <Link to="/contact-us" className="page-link">Contact Us</Link> page 
                  for more support options.
                </p>
              </div>
            </section>
          </div>
        </div>
      </main>

      <footer className="footer">
        <div className="container">
          <p>&copy; 2026 KidsTrail. All rights reserved.</p>
          <div className="footer-links">
            <Link to="/" className="footer-link">Home</Link>
            <Link to="/contact-us" className="footer-link">Contact Us</Link>
            <Link to="/terms-of-use" className="footer-link">Terms of Use</Link>
            <Link to="/privacy-policy" className="footer-link">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default RequestAccountDelete;
